import React, { Component, ErrorInfo, ReactNode } from 'react';
import './ErrorBoundary.css';

// Declare global window properties for gtag
declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: any) => void;
  }
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 * 
 * Provides error handling following the same patterns as the .NET cart templates
 * with user-friendly error messages and recovery options
 */

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Log error to monitoring service (if available)
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: false
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleGoHome = () => {
    // Redirect to a generic landing page or let the user navigate back
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI following .NET cart patterns
      return (
        <div className="error-boundary">
          <div className="error-container">
            <div className="error-icon">
              <i className="fa fa-exclamation-triangle"></i>
            </div>
            <h2 className="error-title">Something went wrong</h2>
            <p className="error-message">
              We're sorry, but something unexpected happened. Please try again or contact support if the problem persists.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details (Development)</summary>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            <div className="error-actions">
              <button 
                className="btn btn-primary" 
                onClick={this.handleRetry}
              >
                <i className="fa fa-refresh"></i> Try Again
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={this.handleGoHome}
              >
                <i className="fa fa-home"></i> Go Home
              </button>
            </div>
            
            <div className="error-support">
              <p>Need help? Contact our support team:</p>
              <p>
                <i className="fa fa-phone"></i> (877) 589-0207
              </p>
              <p>
                <i className="fa fa-envelope"></i> MyLodgeTaxSetup@avalara.com
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 